import { Injectable } from '@angular/core';
import { ComponentStore, OnStoreInit } from '@ngrx/component-store';
import USER_META from 'src/app/authentication/constants';
import { LoginResponsePayload } from 'src/app/authentication/interfaces/login-response-payload';
import { Profile } from 'src/app/pages/users-departments/interfaces/users-profile.models';
import { UserState, User } from './user.model';

@Injectable({
  providedIn: 'root'
})
export default class GlobalStore extends ComponentStore<UserState> implements OnStoreInit {
  constructor() {
    super({ user: {} as User });
  }

  public ngrxOnStoreInit(): void {
    const userData = this.getUserFromLocalStorage();

    if (!userData) return;
    this.setState((state) => ({
      user: {
        ...state,
        ...userData
      }
    }));
  }

  public userLogin(loginPayload: LoginResponsePayload): void {
    this.patchState((state) => ({
      user: {
        ...state.user,
        id: loginPayload.id,
        username: loginPayload.username,
        email: loginPayload.email,
        name: loginPayload.name,
        jwt: loginPayload.jwt,
        wsKey: loginPayload.wsKey,
        scopes: loginPayload.scopes,
        departmentId: loginPayload.departmentId,
        isAdmin: loginPayload.isAdmin
      }
    }));
    this.saveUserToLocalStorage(this.get().user);
  }

  public userLogout(): void {
    this.patchState((state) => ({
      user: {
        ...state.user,
        id: undefined,
        username: undefined,
        email: undefined,
        name: undefined,
        jwt: undefined,
        wsKey: undefined,
        scopes: [],
        departmentId: undefined,
        isAdmin: false
      }
    }));
    localStorage.removeItem(USER_META);
  }

  public updateScopes(scopes: string[]): void {
    this.setState((state) => ({
      user: {
        ...state.user,
        scopes: scopes
      }
    }));
    this.saveUserToLocalStorage(this.get().user);
  }

  public updateTheme(isDark: boolean): void {
    this.setState((state) => ({
      user: {
        ...state.user,
        darkTheme: isDark
      }
    }));
    this.saveUserToLocalStorage(this.get().user);
  }

  public updateProfile(profile: Profile): void {
    this.setState((state) => ({
      user: {
        ...state.user,
        name: `${profile.personalDetails.firstName} ${profile.personalDetails.lastName}`,
        profileId: String(profile._id),
        profileImage: profile.personalDetails.image,
        darkTheme: profile.profileConfig.enabledDarkTheme,
        showReviewedEscalations: profile.profileConfig.showReviewedEscalations
      }
    }));
    this.saveUserToLocalStorage(this.get().user);
  }

  private saveUserToLocalStorage(user: User): void {
    localStorage.setItem(USER_META, JSON.stringify(user));
  }

  public getUserFromLocalStorage(): User | null {
    const userMeta = localStorage.getItem(USER_META);
    return userMeta ? JSON.parse(userMeta) : null;
  }

  public get jwt(): string | null {
    if (!this.get().user.jwt) return null;
    return `Bearer ${this.get().user.jwt}`;
  }

  public get userId(): string | undefined {
    return this.get().user.id;
  }

  public get wsKey(): string | undefined {
    return this.get().user.wsKey;
  }

  public get username(): string | undefined {
    return this.get().user.username;
  }

  public get email(): string | undefined {
    return this.get().user.email;
  }

  public get name(): string | undefined {
    return this.get().user.name;
  }

  public get departmentId(): string | undefined {
    return this.get().user.departmentId;
  }

  public get isAdmin(): boolean {
    return this.get().user.isAdmin;
  }

  public get scopes(): string[] {
    return this.get().user.scopes;
  }

  public get profileId(): string | undefined {
    return this.get().user.profileId;
  }

  public get profileImage(): File | undefined {
    return this.get().user.profileImage;
  }

  public get darkTheme(): boolean {
    return this.get().user.darkTheme;
  }

  public userHasPermission(scope?: string): boolean {
    if (!scope) return true;

    const scopes = this.scopes;

    if (!scopes) return false;

    if (scope.indexOf('*') !== -1) {
      return scopes.some((userScope: string) => {
        return (userScope as string).match(new RegExp(scope)) !== null;
      });
    }

    return scopes.indexOf(scope) !== -1;
  }
}
